// The Ancient Ocean Well: a pseudo-3D canvas well with living water.
// Public API: new WellScene(canvas, opts); setLevel(frac, {instant}); drop({size, onImpact}); ripple(u, v, strength);
// splashSmall(); destroy().

const TAU = Math.PI * 2;
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const lerp = (a, b, t) => a + (b - a) * t;

function mulberry32(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export const prefersReducedMotion = () =>
  typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches;

export class WellScene {
  constructor(canvas, opts = {}) {
    this.canvas = canvas;
    this.ctx = canvas.getContext("2d");
    this.opts = { hero: false, seed: 1337, ambientDrips: false, interactive: true, ...opts };
    this.reduced = prefersReducedMotion();
    this.level = this.opts.initialLevel ?? 0.25; // displayed fraction 0..1
    this.target = this.level;
    this.bump = 0; // temporary surge after an impact
    this.time = 0;
    this.ripples = [];
    this.drops = [];
    this.particles = [];
    this.motes = [];
    this.flashes = [];
    this.mist = [];
    this.nextMist = 0;
    this.sprite = WellScene.#makeSprite();
    this.running = false;
    this.visible = true;
    this.lastTs = 0;
    this.nextDrip = 3;
    this.pointerRippleAt = 0;
    this.rng = mulberry32(this.opts.seed);
    this.stones = this.#makeStones();
    this.#makeMotes();

    this.ro = new ResizeObserver(() => this.#resize());
    this.ro.observe(canvas);
    this.#resize();

    this.io = new IntersectionObserver((entries) => {
      this.visible = entries.some((e) => e.isIntersecting);
      if (this.visible) this.#kick();
    });
    this.io.observe(canvas);

    this.onVis = () => {
      if (!document.hidden) this.#kick();
    };
    document.addEventListener("visibilitychange", this.onVis);

    this.mq = typeof matchMedia !== "undefined" ? matchMedia("(prefers-reduced-motion: reduce)") : null;
    this.onMq = () => {
      this.reduced = this.mq.matches;
      this.#kick();
    };
    this.mq?.addEventListener?.("change", this.onMq);

    if (this.opts.interactive) {
      this.onPointer = (e) => this.#pointer(e);
      canvas.addEventListener("pointermove", this.onPointer);
      canvas.addEventListener("pointerdown", this.onPointer);
    }
    this.#kick();
  }

  destroy() {
    this.running = false;
    this.destroyed = true;
    cancelAnimationFrame(this.raf);
    this.ro.disconnect();
    this.io.disconnect();
    document.removeEventListener("visibilitychange", this.onVis);
    this.mq?.removeEventListener?.("change", this.onMq);
    if (this.onPointer) {
      this.canvas.removeEventListener("pointermove", this.onPointer);
      this.canvas.removeEventListener("pointerdown", this.onPointer);
    }
  }

  /** Set the water level as a fraction 0..1 of the shaft. */
  setLevel(frac, { instant = false } = {}) {
    this.target = clamp(frac, 0.02, 0.98);
    if (instant || this.reduced) this.level = this.target;
    this.#kick();
  }

  /** Drop a droplet from above. size 0.5..2. onImpact is called when it hits the water. */
  drop({ size = 1, onImpact } = {}) {
    const ang = this.rng() * TAU;
    const rad = Math.sqrt(this.rng()) * 0.55;
    const u = Math.cos(ang) * rad;
    const v = Math.sin(ang) * rad * 0.8 - 0.2;
    if (this.reduced) {
      this.ripple(u, v, size * 0.8);
      onImpact?.();
      this.#kick();
      return;
    }
    this.drops.push({ u, v, y: -30 - this.rng() * 40, vy: 140 + this.rng() * 80, size: clamp(size, 0.4, 2.2), onImpact, trail: [] });
    this.#kick();
  }

  /** A small surface disturbance (used for sells). */
  splashSmall() {
    const u = (this.rng() - 0.5) * 0.9;
    const v = (this.rng() - 0.5) * 0.7;
    this.ripple(u, v, 0.45);
    if (!this.reduced) this.#spawnSplash(u, v, 0.35, 5);
  }

  ripple(u, v, strength = 1) {
    this.ripples.push({ u, v, t: 0, strength, life: 2.2 + strength * 0.8 });
    if (this.ripples.length > 40) this.ripples.shift();
    this.#kick();
  }

  // ---------------------------------------------------------------- internals
  static #makeSprite() {
    const c = document.createElement("canvas");
    c.width = c.height = 64;
    const x = c.getContext("2d");
    const g = x.createRadialGradient(32, 32, 0, 32, 32, 32);
    g.addColorStop(0, "rgba(235,255,255,1)");
    g.addColorStop(0.25, "rgba(110,245,255,0.8)");
    g.addColorStop(0.6, "rgba(0,229,255,0.18)");
    g.addColorStop(1, "rgba(0,229,255,0)");
    x.fillStyle = g;
    x.fillRect(0, 0, 64, 64);
    return c;
  }

  #kick() {
    if (this.destroyed) return;
    if (!this.running) {
      this.running = true;
      this.lastTs = performance.now();
      this.raf = requestAnimationFrame((t) => this.#frame(t));
    }
  }

  #resize() {
    const rect = this.canvas.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.w = Math.max(1, rect.width);
    this.h = Math.max(1, rect.height);
    this.canvas.width = Math.round(this.w * dpr);
    this.canvas.height = Math.round(this.h * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.#layout();
    this.#kick();
  }

  #layout() {
    const { w, h } = this;
    const hero = this.opts.hero;
    const R = Math.min(w * (hero ? 0.36 : 0.42), h * (hero ? 0.5 : 0.48));
    this.g = {
      cx: w / 2,
      cy: h * (hero ? 0.4 : 0.37),
      R, // rim outer radius
      r: R * 0.78, // opening radius
      k: 0.4, // vertical squash (camera tilt)
      lip: R * 0.13, // rim cap thickness
      bodyH: Math.min(h * 0.36, R * 0.95),
    };
  }

  #makeStones() {
    const stones = [];
    let a = -Math.PI;
    while (a < Math.PI - 0.01) {
      const span = 0.24 + this.rng() * 0.16;
      const end = Math.min(a + span, Math.PI);
      stones.push({ a0: a, a1: end, shade: this.rng(), chip: this.rng() });
      a = end;
    }
    return stones;
  }

  #makeMotes() {
    const n = this.opts.hero ? 70 : 46;
    for (let i = 0; i < n; i++) {
      this.motes.push({
        x: this.rng(),
        y: this.rng(),
        z: 0.3 + this.rng() * 0.7,
        s: 0.4 + this.rng() * 1.6,
        p: this.rng() * TAU,
      });
    }
  }

  #pointer(e) {
    if (this.reduced) return;
    const now = performance.now();
    if (e.type === "pointermove" && now - this.pointerRippleAt < 160) return;
    const rect = this.canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const W = this.#water();
    const u = (x - W.cx) / W.rx;
    const v = (y - W.cy) / W.ry;
    if (u * u + v * v > 1) return;
    // only where the water is actually visible inside the opening
    const g = this.g;
    const ou = (x - g.cx) / g.r;
    const ov = (y - g.cy) / (g.r * g.k);
    if (ou * ou + ov * ov > 1) return;
    this.pointerRippleAt = now;
    this.ripple(u, v, e.type === "pointerdown" ? 0.9 : 0.25);
    if (e.type === "pointerdown") this.#spawnSplash(u, v, 0.5, 8);
  }

  /** Current water ellipse in screen space. depth 0 = at rim, 1 = deepest. */
  #water() {
    const g = this.g;
    const lvl = clamp(this.level + this.bump, 0, 1);
    const depth = 1 - lvl;
    const scale = 1 - 0.32 * depth;
    const bob = this.reduced ? 0 : Math.sin(this.time * 1.3) * 0.6;
    return {
      cx: g.cx,
      cy: g.cy + depth * g.r * g.k * 1.25 + g.r * g.k * 0.08 + bob,
      rx: g.r * scale,
      ry: g.r * g.k * scale,
      depth,
    };
  }

  #toScreen(u, v) {
    const W = this.#water();
    return { x: W.cx + u * W.rx, y: W.cy + v * W.ry };
  }

  #spawnSplash(u, v, size, count) {
    const p = this.#toScreen(u, v);
    for (let i = 0; i < count; i++) {
      const a = -Math.PI / 2 + (this.rng() - 0.5) * 2.2;
      const sp = (90 + this.rng() * 170) * (0.6 + size * 0.5);
      this.particles.push({
        x: p.x,
        y: p.y,
        vx: Math.cos(a) * sp * 0.75,
        vy: Math.sin(a) * sp,
        y0: p.y,
        r: 1 + this.rng() * 2.2 * size,
        life: 0.7 + this.rng() * 0.6,
        t: 0,
      });
    }
    if (this.particles.length > 260) this.particles.splice(0, this.particles.length - 260);
    this.flashes.push({ x: p.x, y: p.y, t: 0, size });
  }

  #frame(ts) {
    if (this.destroyed) return;
    const dt = clamp((ts - this.lastTs) / 1000, 0, 0.05);
    this.lastTs = ts;
    if (!this.visible || document.hidden) {
      this.running = false;
      return;
    }
    this.#update(dt);
    this.#draw();
    const busy =
      !this.reduced ||
      Math.abs(this.target - this.level) > 0.0005 ||
      this.ripples.length ||
      this.drops.length ||
      this.particles.length;
    if (busy) {
      this.raf = requestAnimationFrame((t) => this.#frame(t));
    } else {
      this.running = false;
    }
  }

  #update(dt) {
    this.time += this.reduced ? 0 : dt;
    // ease the level towards target (critically damped feel)
    const ease = 1 - Math.exp(-dt * 2.2);
    this.level = this.reduced ? this.target : lerp(this.level, this.target, ease);
    this.bump *= Math.exp(-dt * 3);

    // ripples
    for (const r of this.ripples) r.t += dt || 0.016;
    this.ripples = this.ripples.filter((r) => r.t < r.life);

    // drops
    const W = this.#water();
    for (const d of this.drops) {
      d.vy += 900 * dt;
      d.y += d.vy * dt;
      const tgt = this.#toScreen(d.u, d.v);
      d.trail.push(d.y);
      if (d.trail.length > 6) d.trail.shift();
      if (d.y >= tgt.y) {
        d.done = true;
        this.ripple(d.u, d.v, 0.7 + d.size * 0.6);
        setTimeout(() => this.ripple(d.u, d.v, 0.4 + d.size * 0.3), 180);
        this.#spawnSplash(d.u, d.v, d.size, Math.round(10 + d.size * 12));
        this.bump += 0.01 * d.size;
        d.onImpact?.();
      }
    }
    this.drops = this.drops.filter((d) => !d.done);

    // splash particles
    for (const p of this.particles) {
      p.t += dt;
      p.vy += 620 * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
    }
    this.particles = this.particles.filter((p) => p.t < p.life && !(p.vy > 0 && p.y > p.y0 + 2));
    for (const f of this.flashes) f.t += dt;
    this.flashes = this.flashes.filter((f) => f.t < 0.6);

    // rising mist off the water surface
    if (!this.reduced) {
      this.nextMist -= dt;
      if (this.nextMist <= 0) {
        this.nextMist = 0.35 + this.rng() * 0.4;
        const u = (this.rng() - 0.5) * 1.5;
        this.mist.push({ u, x: 0, y: 0, born: false, vy: -(6 + this.rng() * 9), vx: (this.rng() - 0.5) * 5, r: W.rx * (0.12 + this.rng() * 0.12), t: 0, life: 3 + this.rng() * 2.5 });
      }
      for (const m of this.mist) {
        if (!m.born) {
          m.born = true;
          m.x = W.cx + m.u * W.rx * 0.6;
          m.y = W.cy;
        }
        m.t += dt;
        m.x += m.vx * dt;
        m.y += m.vy * dt;
        m.r += dt * 5;
      }
      this.mist = this.mist.filter((m) => m.t < m.life);
    }

    // motes
    if (!this.reduced) {
      for (const m of this.motes) {
        m.y -= dt * 0.012 * m.z;
        m.x += Math.sin(this.time * 0.4 + m.p) * dt * 0.004;
        if (m.y < -0.02) {
          m.y = 1.02;
          m.x = this.rng();
        }
      }
    }

    // ambient condensation drips (decorative, home hero only)
    if (this.opts.ambientDrips && !this.reduced) {
      this.nextDrip -= dt;
      if (this.nextDrip <= 0) {
        this.nextDrip = 3.5 + this.rng() * 4;
        this.drop({ size: 0.45 });
      }
    }
    void W;
  }

  // ---------------------------------------------------------------- drawing
  #draw() {
    const { ctx, w, h } = this;
    ctx.clearRect(0, 0, w, h);
    this.#drawBackdrop();
    this.#drawGround();
    this.#drawBody();
    this.#drawRim();
    this.#drawInterior();
    this.#drawMist();
    this.#drawSplash();
    this.#drawDrops();
    this.#drawMotes();
  }

  #drawBackdrop() {
    const { ctx, w, h, g } = this;
    // god rays from above into the well
    ctx.save();
    ctx.globalCompositeOperation = "lighter";
    const rays = 4;
    for (let i = 0; i < rays; i++) {
      const sway = this.reduced ? 0 : Math.sin(this.time * 0.25 + i * 1.7) * g.R * 0.08;
      const topX = g.cx + (i - (rays - 1) / 2) * g.R * 0.35 + sway;
      const spread = g.R * (0.1 + 0.05 * i);
      const grd = ctx.createLinearGradient(0, 0, 0, g.cy);
      grd.addColorStop(0, "rgba(0,229,255,0)");
      grd.addColorStop(0.6, "rgba(0,229,255,0.025)");
      grd.addColorStop(1, "rgba(0,229,255,0.06)");
      ctx.fillStyle = grd;
      ctx.beginPath();
      ctx.moveTo(topX - spread * 0.4, 0);
      ctx.lineTo(topX + spread * 0.4, 0);
      ctx.lineTo(g.cx + (i - 1.5) * g.r * 0.3 + spread, g.cy);
      ctx.lineTo(g.cx + (i - 1.5) * g.r * 0.3 - spread, g.cy);
      ctx.closePath();
      ctx.fill();
    }
    ctx.restore();
    // halo behind the well
    const haloR = Math.min(g.R * 1.6, w / 2, h * 0.6);
    const halo = ctx.createRadialGradient(g.cx, g.cy, g.R * 0.2, g.cx, g.cy, haloR);
    const pulse = this.reduced ? 0.5 : 0.5 + 0.5 * Math.sin(this.time * 0.9);
    halo.addColorStop(0, `rgba(0,229,255,${0.07 + 0.03 * pulse})`);
    halo.addColorStop(1, "rgba(0,229,255,0)");
    ctx.fillStyle = halo;
    ctx.fillRect(0, 0, w, h);
  }

  #drawGround() {
    const { ctx, g } = this;
    const baseY = g.cy + g.lip * 0.6 + g.bodyH;
    const Rb = g.R * 0.95;
    ctx.save();
    const sh = ctx.createRadialGradient(g.cx, baseY, 0, g.cx, baseY, Rb * 1.7);
    sh.addColorStop(0, "rgba(0,0,0,0.55)");
    sh.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = sh;
    ctx.beginPath();
    ctx.ellipse(g.cx, baseY, Rb * 1.7, Rb * g.k * 1.5, 0, 0, TAU);
    ctx.fill();
    // glowing tidal ring on the floor
    const pulse = this.reduced ? 0.5 : 0.5 + 0.5 * Math.sin(this.time * 1.4);
    ctx.strokeStyle = `rgba(0,229,255,${0.12 + 0.12 * pulse})`;
    ctx.lineWidth = 1.5;
    ctx.shadowColor = "rgba(0,229,255,0.8)";
    ctx.shadowBlur = 18;
    ctx.beginPath();
    ctx.ellipse(g.cx, baseY + 4, Rb * 1.32, Rb * g.k * 1.22, 0, 0, TAU);
    ctx.stroke();
    ctx.shadowBlur = 0;
    ctx.strokeStyle = "rgba(0,229,255,0.07)";
    ctx.beginPath();
    ctx.ellipse(g.cx, baseY + 8, Rb * 1.6, Rb * g.k * 1.45, 0, 0, TAU);
    ctx.stroke();
    ctx.restore();
  }

  #drawBody() {
    const { ctx, g } = this;
    const Rb = g.R * 0.95;
    const y0 = g.cy + g.lip * 0.5;
    const y1 = y0 + g.bodyH;
    const ry = Rb * g.k;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(g.cx - Rb, y0);
    ctx.lineTo(g.cx - Rb, y1);
    ctx.ellipse(g.cx, y1, Rb, ry, 0, Math.PI, 0, true);
    ctx.lineTo(g.cx + Rb, y0);
    ctx.ellipse(g.cx, y0, Rb, ry, 0, 0, Math.PI, false);
    ctx.closePath();
    const grd = ctx.createLinearGradient(g.cx - Rb, 0, g.cx + Rb, 0);
    grd.addColorStop(0, "#07090f");
    grd.addColorStop(0.22, "#1a2032");
    grd.addColorStop(0.45, "#252d42");
    grd.addColorStop(0.7, "#161c2b");
    grd.addColorStop(1, "#05070c");
    ctx.fillStyle = grd;
    ctx.fill();
    ctx.clip();

    // stone courses (front half arcs) and staggered vertical joints
    const rows = 5;
    const rowH = g.bodyH / rows;
    ctx.lineWidth = 1.4;
    for (let i = 0; i <= rows; i++) {
      const y = y0 + ry + i * rowH;
      ctx.strokeStyle = "rgba(2,4,8,0.85)";
      ctx.beginPath();
      ctx.ellipse(g.cx, y - ry, Rb, ry, 0, 0, Math.PI, false);
      ctx.stroke();
      ctx.strokeStyle = "rgba(120,140,170,0.07)";
      ctx.beginPath();
      ctx.ellipse(g.cx, y - ry + 1.5, Rb, ry, 0, 0.15, Math.PI - 0.15, false);
      ctx.stroke();
    }
    for (let i = 0; i < rows; i++) {
      const n = 9;
      for (let j = 0; j < n; j++) {
        const a = ((j + (i % 2) * 0.5) / n) * Math.PI;
        const x = g.cx + Math.cos(a) * Rb;
        const yTop = y0 + i * rowH + Math.sin(a) * ry;
        ctx.strokeStyle = "rgba(2,4,8,0.8)";
        ctx.beginPath();
        ctx.moveTo(x, yTop + 1);
        ctx.lineTo(x, yTop + rowH - 1);
        ctx.stroke();
      }
    }
    // engraved glowing band
    const bandY = y0 + g.bodyH * 0.52;
    const glow = this.reduced ? 0.6 : 0.45 + 0.25 * Math.sin(this.time * 1.1);
    ctx.strokeStyle = `rgba(0,229,255,${0.22 * glow + 0.1})`;
    ctx.shadowColor = "#00e5ff";
    ctx.shadowBlur = 12;
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.ellipse(g.cx, bandY, Rb, ry, 0, 0.12, Math.PI - 0.12, false);
    ctx.stroke();
    ctx.shadowBlur = 0;
    // rune glyphs along the band
    ctx.fillStyle = `rgba(0,229,255,${0.18 + 0.2 * glow})`;
    for (let j = 1; j < 8; j++) {
      const a = (j / 8) * Math.PI;
      const x = g.cx + Math.cos(a) * Rb * 0.98;
      const y = bandY + Math.sin(a) * ry - 9;
      const s = Math.sin(a) * 4 + 1.5;
      ctx.fillRect(x - 0.6, y - s, 1.2, s * 1.6);
      ctx.fillRect(x - s * 0.6, y - s * 0.2, s * 1.2, 1);
    }
    // sea-moss / wet sheen at the top of the body
    const wet = ctx.createLinearGradient(0, y0, 0, y0 + g.bodyH * 0.5);
    wet.addColorStop(0, "rgba(0,229,255,0.07)");
    wet.addColorStop(1, "rgba(0,229,255,0)");
    ctx.fillStyle = wet;
    ctx.fillRect(g.cx - Rb, y0, Rb * 2, g.bodyH);
    // ambient occlusion under the rim
    const ao = ctx.createLinearGradient(0, y0, 0, y0 + ry * 2.2);
    ao.addColorStop(0, "rgba(0,0,0,0.65)");
    ao.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = ao;
    ctx.fillRect(g.cx - Rb, y0, Rb * 2, ry * 3);
    ctx.restore();
  }

  #drawRim() {
    const { ctx, g } = this;
    const R = g.R;
    const r = g.r;
    const k = g.k;
    // front lip faces (vertical) for stones on the near half
    for (const s of this.stones) {
      const a0 = Math.max(s.a0, 0);
      const a1 = Math.min(s.a1, Math.PI);
      if (a1 <= a0) continue;
      ctx.beginPath();
      const steps = 8;
      for (let i = 0; i <= steps; i++) {
        const a = lerp(a0, a1, i / steps);
        const x = g.cx + Math.cos(a) * R;
        const y = g.cy + Math.sin(a) * R * k;
        i ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
      }
      for (let i = steps; i >= 0; i--) {
        const a = lerp(a0, a1, i / steps);
        ctx.lineTo(g.cx + Math.cos(a) * R, g.cy + Math.sin(a) * R * k + g.lip);
      }
      ctx.closePath();
      const mid = (a0 + a1) / 2;
      const light = 0.55 + 0.45 * Math.cos(mid - Math.PI * 0.62); // lit from front-left
      const base = 26 + s.shade * 14;
      ctx.fillStyle = `rgb(${base * light + 6 | 0},${(base + 6) * light + 8 | 0},${(base + 18) * light + 12 | 0})`;
      ctx.fill();
      ctx.strokeStyle = "rgba(1,2,6,0.9)";
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }
    // top surface ring, stone by stone
    for (const s of this.stones) {
      ctx.beginPath();
      const steps = 10;
      for (let i = 0; i <= steps; i++) {
        const a = lerp(s.a0, s.a1, i / steps);
        const x = g.cx + Math.cos(a) * R;
        const y = g.cy + Math.sin(a) * R * k;
        i ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
      }
      for (let i = steps; i >= 0; i--) {
        const a = lerp(s.a0, s.a1, i / steps);
        ctx.lineTo(g.cx + Math.cos(a) * r, g.cy + Math.sin(a) * r * k);
      }
      ctx.closePath();
      const mid = (s.a0 + s.a1) / 2;
      const facing = 0.75 + 0.25 * Math.sin(mid); // near stones brighter
      const base = 48 + s.shade * 22;
      const grd = ctx.createLinearGradient(
        g.cx + Math.cos(mid) * R,
        g.cy + Math.sin(mid) * R * k,
        g.cx + Math.cos(mid) * r,
        g.cy + Math.sin(mid) * r * k
      );
      grd.addColorStop(0, `rgb(${base * facing | 0},${(base + 6) * facing | 0},${(base + 20) * facing | 0})`);
      grd.addColorStop(1, `rgb(${(base - 14) * facing | 0},${(base - 4) * facing | 0},${(base + 10) * facing | 0})`);
      ctx.fillStyle = grd;
      ctx.fill();
      ctx.strokeStyle = "rgba(2,3,8,0.95)";
      ctx.lineWidth = 2;
      ctx.stroke();
      // chips / highlights
      if (s.chip > 0.55) {
        const a = lerp(s.a0, s.a1, 0.3 + s.chip * 0.3);
        const rr = lerp(r, R, 0.55);
        ctx.fillStyle = "rgba(170,190,220,0.08)";
        ctx.beginPath();
        ctx.ellipse(g.cx + Math.cos(a) * rr, g.cy + Math.sin(a) * rr * k, (R - r) * 0.18, (R - r) * 0.07, 0, 0, TAU);
        ctx.fill();
      }
    }
    // outer edge highlight
    ctx.strokeStyle = "rgba(160,190,230,0.16)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.ellipse(g.cx, g.cy, R, R * k, 0, Math.PI * 1.05, Math.PI * 1.95);
    ctx.stroke();
    // cyan light spilling over the inner lip
    const glow = this.reduced ? 0.6 : 0.5 + 0.2 * Math.sin(this.time * 1.7);
    ctx.save();
    ctx.shadowColor = "#00e5ff";
    ctx.shadowBlur = 16;
    ctx.strokeStyle = `rgba(0,229,255,${0.35 * glow + 0.15})`;
    ctx.lineWidth = 1.6;
    ctx.beginPath();
    ctx.ellipse(g.cx, g.cy, r, r * k, 0, 0, TAU);
    ctx.stroke();
    ctx.restore();
  }

  #openingPath() {
    const { ctx, g } = this;
    ctx.beginPath();
    ctx.ellipse(g.cx, g.cy, g.r - 1, g.r * g.k - 1, 0, 0, TAU);
  }

  #drawInterior() {
    const { ctx, g } = this;
    const W = this.#water();
    ctx.save();
    this.#openingPath();
    ctx.clip();

    // back wall of the shaft
    const top = g.cy - g.r * g.k;
    const wall = ctx.createLinearGradient(0, top, 0, W.cy + W.ry);
    wall.addColorStop(0, "#1a2133");
    wall.addColorStop(0.5, "#0b0f1a");
    wall.addColorStop(1, "#04060b");
    ctx.fillStyle = wall;
    ctx.fillRect(g.cx - g.r, top, g.r * 2, g.r * g.k * 2 + 4);

    // masonry courses on the inner back wall, receding in perspective
    const courses = 9;
    for (let i = 1; i <= courses; i++) {
      const t = i / courses; // depth 0..1
      const sc = 1 - 0.32 * t;
      const cy = g.cy + t * g.r * g.k * 1.25 + g.r * g.k * 0.08;
      if (t > W.depth + 0.04) break;
      ctx.strokeStyle = `rgba(0,0,0,${0.55 + t * 0.3})`;
      ctx.lineWidth = 1.3;
      ctx.beginPath();
      ctx.ellipse(g.cx, cy, g.r * sc, g.r * g.k * sc, 0, Math.PI, TAU);
      ctx.stroke();
      ctx.strokeStyle = `rgba(110,140,180,${0.06 * (1 - t)})`;
      ctx.beginPath();
      ctx.ellipse(g.cx, cy + 1.5, g.r * sc, g.r * g.k * sc, 0, Math.PI * 1.1, Math.PI * 1.9);
      ctx.stroke();
      // vertical joints between this course and the previous one
      const tp = (i - 1) / courses;
      const scp = 1 - 0.32 * tp;
      const cyp = g.cy + tp * g.r * g.k * 1.25 + g.r * g.k * 0.08;
      const n = 10;
      ctx.strokeStyle = "rgba(0,0,0,0.6)";
      ctx.beginPath();
      for (let j = 0; j < n; j++) {
        const a = Math.PI + ((j + (i % 2) * 0.5 + 0.25) / n) * Math.PI;
        ctx.moveTo(g.cx + Math.cos(a) * g.r * scp, cyp + Math.sin(a) * g.r * g.k * scp);
        ctx.lineTo(g.cx + Math.cos(a) * g.r * sc, cy + Math.sin(a) * g.r * g.k * sc);
      }
      ctx.stroke();
    }
    // cyan bounce light on the wall just above the waterline (follows the curve of the water)
    ctx.save();
    ctx.globalCompositeOperation = "lighter";
    for (let i = 1; i <= 7; i++) {
      ctx.strokeStyle = `rgba(0,229,255,${0.05 * (1 - i / 8)})`;
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.ellipse(W.cx, W.cy - i * 3.5, W.rx * (1 + i * 0.004), W.ry, 0, Math.PI, TAU);
      ctx.stroke();
    }
    ctx.restore();
    // ---- water
    ctx.save();
    ctx.beginPath();
    ctx.ellipse(W.cx, W.cy, W.rx, W.ry, 0, 0, TAU);
    ctx.clip();
    const deep = ctx.createRadialGradient(W.cx, W.cy - W.ry * 0.3, W.rx * 0.05, W.cx, W.cy, W.rx);
    deep.addColorStop(0, "#0aa9c4");
    deep.addColorStop(0.35, "#06647d");
    deep.addColorStop(0.75, "#032f44");
    deep.addColorStop(1, "#021622");
    ctx.fillStyle = deep;
    ctx.fillRect(W.cx - W.rx, W.cy - W.ry, W.rx * 2, W.ry * 2);

    // caustics: interfering sine bands drawn additively
    ctx.globalCompositeOperation = "lighter";
    const t = this.time;
    ctx.lineWidth = Math.max(1, W.rx * 0.012);
    for (let pass = 0; pass < 2; pass++) {
      const lines = 9;
      for (let i = 0; i < lines; i++) {
        const base = -1 + (i + 0.5) * (2 / lines);
        ctx.strokeStyle = `rgba(120,250,255,${0.05 + 0.03 * Math.sin(t * 1.3 + i)})`;
        ctx.beginPath();
        const steps = 28;
        for (let s = 0; s <= steps; s++) {
          const q = -1 + (2 * s) / steps;
          let u;
          let v;
          if (pass === 0) {
            u = q;
            v = base + 0.09 * Math.sin(q * 5 + t * 1.6 + i * 1.3) + 0.05 * Math.sin(q * 11 - t * 2.1);
          } else {
            v = q;
            u = base + 0.08 * Math.sin(q * 6 - t * 1.4 + i * 0.9) + 0.04 * Math.sin(q * 13 + t * 2.4);
          }
          const x = W.cx + u * W.rx;
          const y = W.cy + v * W.ry;
          s ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
        }
        ctx.stroke();
      }
    }
    // shimmering glints
    for (let i = 0; i < 14; i++) {
      const ph = i * 2.39996;
      const u = Math.cos(ph) * ((i * 0.071) % 0.9);
      const v = Math.sin(ph) * ((i * 0.053) % 0.8);
      const a = Math.max(0, Math.sin(t * 2.2 + i * 1.7));
      if (a < 0.4) continue;
      ctx.fillStyle = `rgba(210,255,255,${(a - 0.4) * 0.5})`;
      ctx.beginPath();
      ctx.ellipse(W.cx + u * W.rx, W.cy + v * W.ry, W.rx * 0.03, W.ry * 0.012 + 0.5, 0, 0, TAU);
      ctx.fill();
    }
    // slow specular sheen sliding across the surface + a bright core where the light hits
    const shx = W.cx + Math.sin(t * 0.37) * W.rx * 0.45;
    const shy = W.cy - W.ry * 0.25 + Math.cos(t * 0.29) * W.ry * 0.15;
    const sheen = ctx.createRadialGradient(shx, shy, 0, shx, shy, W.rx * 0.55);
    sheen.addColorStop(0, "rgba(210,255,255,0.16)");
    sheen.addColorStop(1, "rgba(210,255,255,0)");
    ctx.fillStyle = sheen;
    ctx.fillRect(W.cx - W.rx, W.cy - W.ry, W.rx * 2, W.ry * 2);
    const core = ctx.createRadialGradient(W.cx, W.cy, 0, W.cx, W.cy, W.rx * 0.35);
    core.addColorStop(0, `rgba(160,255,255,${0.16 + 0.05 * Math.sin(t * 1.1)})`);
    core.addColorStop(1, "rgba(0,229,255,0)");
    ctx.fillStyle = core;
    ctx.beginPath();
    ctx.ellipse(W.cx, W.cy, W.rx * 0.35, W.ry * 0.35, 0, 0, TAU);
    ctx.fill();
    // ripples
    for (const r of this.ripples) {
      const p = r.t / r.life;
      for (let ring = 0; ring < 3; ring++) {
        const rad = (r.t * (0.32 + r.strength * 0.08) - ring * 0.07) * W.rx;
        if (rad <= 0) continue;
        const alpha = (1 - p) * (0.55 - ring * 0.15) * Math.min(1, r.strength + 0.2);
        if (alpha <= 0) continue;
        ctx.strokeStyle = `rgba(160,250,255,${alpha})`;
        ctx.lineWidth = Math.max(0.8, (2.2 - ring * 0.6) * (1 - p) * (0.6 + r.strength * 0.4));
        ctx.beginPath();
        ctx.ellipse(W.cx + r.u * W.rx, W.cy + r.v * W.ry, rad, rad * (W.ry / W.rx), 0, 0, TAU);
        ctx.stroke();
      }
    }
    // impact flashes
    for (const f of this.flashes) {
      const a = 1 - f.t / 0.6;
      const rad = (12 + f.size * 18) * (0.6 + f.t);
      const gl = ctx.createRadialGradient(f.x, f.y, 0, f.x, f.y, rad);
      gl.addColorStop(0, `rgba(180,255,255,${0.55 * a})`);
      gl.addColorStop(1, "rgba(0,229,255,0)");
      ctx.fillStyle = gl;
      ctx.beginPath();
      ctx.ellipse(f.x, f.y, rad, rad * g.k, 0, 0, TAU);
      ctx.fill();
    }
    ctx.globalCompositeOperation = "source-over";
    // edge darkening where the water meets the wall
    const edge = ctx.createRadialGradient(W.cx, W.cy, W.rx * 0.6, W.cx, W.cy, W.rx);
    edge.addColorStop(0, "rgba(0,0,0,0)");
    edge.addColorStop(1, "rgba(0,4,10,0.55)");
    ctx.fillStyle = edge;
    ctx.fillRect(W.cx - W.rx, W.cy - W.ry, W.rx * 2, W.ry * 2);
    ctx.restore();

    // glowing waterline along the back wall
    ctx.save();
    ctx.shadowColor = "#00e5ff";
    ctx.shadowBlur = 14;
    ctx.strokeStyle = "rgba(90,245,255,0.75)";
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.ellipse(W.cx, W.cy, W.rx, W.ry, 0, Math.PI * 1.02, Math.PI * 1.98);
    ctx.stroke();
    ctx.restore();

    // inner shadow cast by the rim onto the shaft
    const shade = ctx.createRadialGradient(g.cx, g.cy - g.r * g.k * 0.2, g.r * 0.65, g.cx, g.cy, g.r * 1.05);
    shade.addColorStop(0, "rgba(0,0,0,0)");
    shade.addColorStop(1, "rgba(0,0,0,0.5)");
    ctx.fillStyle = shade;
    ctx.fillRect(g.cx - g.r, g.cy - g.r * g.k, g.r * 2, g.r * g.k * 2);
    ctx.restore();
  }

  #drawSplash() {
    if (!this.particles.length) return;
    const { ctx, g } = this;
    ctx.save();
    // clip: the column above the opening plus the opening itself
    ctx.beginPath();
    ctx.rect(g.cx - g.r, 0, g.r * 2, g.cy);
    ctx.ellipse(g.cx, g.cy, g.r, g.r * g.k, 0, 0, TAU);
    ctx.clip();
    ctx.globalCompositeOperation = "lighter";
    for (const p of this.particles) {
      const a = 1 - p.t / p.life;
      const s = p.r * (1.2 + 0.9 * a) * 2.2;
      ctx.globalAlpha = 0.9 * a;
      ctx.drawImage(this.sprite, p.x - s / 2, p.y - s / 2, s, s);
    }
    ctx.restore();
  }

  #drawMist() {
    if (!this.mist.length) return;
    const { ctx, g } = this;
    ctx.save();
    ctx.beginPath();
    ctx.rect(g.cx - g.r, 0, g.r * 2, g.cy);
    ctx.ellipse(g.cx, g.cy, g.r, g.r * g.k, 0, 0, TAU);
    ctx.clip();
    ctx.globalCompositeOperation = "lighter";
    for (const m of this.mist) {
      ctx.globalAlpha = Math.sin((m.t / m.life) * Math.PI) * 0.12;
      ctx.drawImage(this.sprite, m.x - m.r, m.y - m.r * 0.7, m.r * 2, m.r * 1.4);
    }
    ctx.restore();
  }

  #drawDrops() {
    if (!this.drops.length) return;
    const { ctx } = this;
    ctx.save();
    for (const d of this.drops) {
      const p = this.#toScreen(d.u, d.v);
      const s = 4 + d.size * 4;
      // trail
      const tr = ctx.createLinearGradient(p.x, d.y - s * 7, p.x, d.y);
      tr.addColorStop(0, "rgba(0,229,255,0)");
      tr.addColorStop(1, "rgba(0,229,255,0.45)");
      ctx.strokeStyle = tr;
      ctx.lineWidth = s * 0.5;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(p.x, d.y - s * 7);
      ctx.lineTo(p.x, d.y - s);
      ctx.stroke();
      // teardrop body
      ctx.shadowColor = "#00e5ff";
      ctx.shadowBlur = 18;
      const body = ctx.createRadialGradient(p.x - s * 0.3, d.y - s * 0.3, 0, p.x, d.y, s * 1.2);
      body.addColorStop(0, "#e9ffff");
      body.addColorStop(0.4, "#5ff3ff");
      body.addColorStop(1, "#00a7c2");
      ctx.fillStyle = body;
      ctx.beginPath();
      ctx.moveTo(p.x, d.y - s * 2.2);
      ctx.bezierCurveTo(p.x + s * 0.4, d.y - s * 1.1, p.x + s, d.y - s * 0.4, p.x + s, d.y + s * 0.15);
      ctx.arc(p.x, d.y + s * 0.15, s, 0, Math.PI, false);
      ctx.bezierCurveTo(p.x - s, d.y - s * 0.4, p.x - s * 0.4, d.y - s * 1.1, p.x, d.y - s * 2.2);
      ctx.fill();
      ctx.shadowBlur = 0;
    }
    ctx.restore();
  }

  #drawMotes() {
    const { ctx, w, h } = this;
    ctx.save();
    ctx.globalCompositeOperation = "lighter";
    for (const m of this.motes) {
      const tw = this.reduced ? 0.6 : 0.5 + 0.5 * Math.sin(this.time * 1.5 + m.p * 3);
      ctx.fillStyle = `rgba(120,240,255,${0.08 + 0.22 * tw * m.z})`;
      ctx.beginPath();
      ctx.arc(m.x * w, m.y * h, m.s * m.z, 0, TAU);
      ctx.fill();
    }
    ctx.restore();
  }
}

/** Map SOL in the well to a visual fill fraction (log scale so small wells still show water). */
export function levelToFraction(sol, refSol = 50) {
  const s = Math.max(0, sol);
  const f = Math.log10(1 + s / 0.02) / Math.log10(1 + refSol / 0.02);
  return 0.2 + 0.76 * Math.min(1, f);
}
